import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { useSalesMembers } from '../hooks/useSalesMembers';
import { PageHeader, Button, Card, Alert, LoadingState, Modal, Badge } from './ui';

const ROLE_LABELS = {
  admin: '管理者',
  sales: '営業',
};

function roleLabel(member) {
  return member.can_manage_members ? ROLE_LABELS.admin : ROLE_LABELS.sales;
}

function statusLabel(member) {
  if (!member.user_is_active) return '無効（アカウント）';
  return member.is_active ? '有効' : '無効';
}

function canDeleteMember(member, currentUserId) {
  return member.user_id !== currentUserId;
}

export default function UserManagement() {
  const { user } = useUser();
  const { refreshMembers } = useSalesMembers();
  const canManage = user?.permissions?.can_manage_sales_members === true;

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'sales' });
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!canManage) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/sales-members/?manage=1');
      if (!res.ok) {
        throw new Error('ユーザー一覧の取得に失敗しました');
      }
      const data = await res.json();
      setMembers(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      setError(err.message || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const resetForm = () => {
    setForm({ name: '', email: '', role: 'sales' });
    setFormErrors({});
  };

  const handleOpenAdd = () => {
    resetForm();
    setSuccess('');
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setError('');
    setSuccess('');

    const errors = {};
    if (!form.name.trim()) errors.name = '名前は必須です';
    if (!form.email.trim()) errors.email = 'メールアドレスは必須です';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiFetch('/sales-members/register-user/', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          role: form.role,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message =
          data.email?.[0] ||
          data.name?.[0] ||
          data.role?.[0] ||
          data.detail ||
          '登録に失敗しました';
        throw new Error(typeof message === 'string' ? message : '登録に失敗しました');
      }
      setShowAddModal(false);
      resetForm();
      setSuccess(`${data.display_name || form.name.trim()} を登録しました`);
      await loadMembers();
      await refreshMembers();
    } catch (err) {
      setError(err.message || '登録に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    setSuccess('');
    try {
      const res = await apiFetch(`/sales-members/${deleteTarget.id}/`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || '削除に失敗しました');
      }
      const name = deleteTarget.display_name;
      setDeleteTarget(null);
      setSuccess(`${name} を削除しました`);
      await loadMembers();
      await refreshMembers();
    } catch (err) {
      setError(err.message || '削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <PageHeader title="ユーザー管理" description="Prodiaで利用するユーザーを管理します" />
        <Alert variant="warning" title="このページにアクセスする権限がありません" />
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-prodia-background">
      <PageHeader
        title="ユーザー管理"
        description="Prodiaで利用するユーザーを管理します"
        actions={
          <Button onClick={handleOpenAdd}>
            <i className="fas fa-plus text-xs" aria-hidden="true" />
            ユーザー追加
          </Button>
        }
      />

      {error && <Alert variant="danger" title={error} />}
      {success && <Alert variant="success" title={success} />}

      <Card padding={false} className="overflow-hidden">
        {loading ? (
          <LoadingState message="読み込み中..." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-prodia-background text-prodia-text-secondary border-b border-prodia-border">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">名前</th>
                  <th className="px-4 py-3 text-left font-medium">メールアドレス</th>
                  <th className="px-4 py-3 text-left font-medium">権限</th>
                  <th className="px-4 py-3 text-left font-medium">状態</th>
                  <th className="px-4 py-3 text-left font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-prodia-text-muted">
                      登録ユーザーがありません
                    </td>
                  </tr>
                ) : (
                  members.map((member) => {
                    const deletable = canDeleteMember(member, user?.id);
                    return (
                      <tr key={member.id} className="border-t border-prodia-border">
                        <td className="px-4 py-3 font-medium text-prodia-text-primary">
                          {member.display_name}
                        </td>
                        <td className="px-4 py-3 text-prodia-text-secondary">{member.user_email}</td>
                        <td className="px-4 py-3">
                          <Badge variant={member.can_manage_members ? 'primary' : 'neutral'}>
                            {roleLabel(member)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={member.is_active && member.user_is_active !== false ? 'success' : 'neutral'}>
                            {statusLabel(member)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {deletable ? (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => setDeleteTarget(member)}
                            >
                              削除
                            </Button>
                          ) : (
                            <span className="text-prodia-text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-xs text-prodia-text-muted">
        新規登録ユーザーはパスワード未設定のため、ログイン開始前に管理者によるパスワード設定が必要です（既存の
        create_users.py 等を利用）。削除したユーザーはログインできなくなり、PP/BPの担当者候補からも除外されます。過去案件の担当者名は保持されます。
      </p>

      <Modal
        open={showAddModal}
        onClose={() => !submitting && setShowAddModal(false)}
        title="ユーザー追加"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={submitting}>
              キャンセル
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              登録
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-prodia-text-secondary mb-1">名前</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-xl text-sm bg-prodia-surface ${
                formErrors.name ? 'border-red-400' : 'border-prodia-border'
              }`}
              placeholder="山田 太郎"
            />
            {formErrors.name && <p className="text-xs text-red-600 mt-1">{formErrors.name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-prodia-text-secondary mb-1">メールアドレス</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-xl text-sm bg-prodia-surface ${
                formErrors.email ? 'border-red-400' : 'border-prodia-border'
              }`}
              placeholder="yamada@example.com"
            />
            {formErrors.email && <p className="text-xs text-red-600 mt-1">{formErrors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-prodia-text-secondary mb-1">権限</label>
            <select
              value={form.role}
              onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
              className="w-full px-3 py-2 border border-prodia-border rounded-xl text-sm bg-prodia-surface"
            >
              <option value="sales">営業</option>
              <option value="admin">管理者</option>
            </select>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="ユーザーを削除しますか？"
        primaryVariant="danger"
        primaryLabel="削除する"
        secondaryLabel="キャンセル"
        onSecondary={() => !deleting && setDeleteTarget(null)}
        onPrimary={handleConfirmDelete}
      >
        <div className="space-y-3 text-sm text-prodia-text-secondary">
          <p>
            <span className="font-semibold text-prodia-text-primary">{deleteTarget?.display_name}</span>
            さんを削除します。
          </p>
          <p className="text-danger font-medium">この操作は取り消せません。DBからユーザー情報が完全に削除されます。</p>
          <p className="text-xs text-prodia-text-muted">
            過去のPP/BP案件に記録された担当者名は変更されません。
          </p>
        </div>
      </Modal>
    </div>
  );
}
